import React, { useEffect, useState } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiFileText,
  FiMail,
  FiPhone,
  FiUploadCloud,
  FiUser,
  FiUserPlus,
  FiX,
} from "react-icons/fi";
import Avatar from "../components/Avatar";
import ComponentLoader from "../components/ComponentLoader";
import { directory } from "../data/hrmMockData";
import { getProfileImageCandidates } from "../utils/profileImages";
import ScreenHeader from "./ScreenHeader";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const initialReferralForm = {
  role: "",
  candidateName: "",
  email: "",
  phone: "",
  notes: "",
};

const ReferralFileField = ({ id, label, required = false, file, onChange }) => (
  <label className="new-hrm-referral-file" htmlFor={id}>
    <span className="new-hrm-referral-label">{label}</span>
    <input
      id={id}
      aria-label={label}
      required={required}
      type="file"
      accept=".pdf,.doc,.docx"
      onChange={(event) => onChange(event.target.files?.[0] || null)}
    />
    <div className="new-hrm-referral-file__control">
      <span className="new-hrm-referral-file__icon">
        <FiUploadCloud aria-hidden="true" />
      </span>
      <div>
        <strong>{file ? file.name : `Upload ${label.toLowerCase()}`}</strong>
        <small>{file ? "Ready to attach" : "PDF, DOC, or DOCX"}</small>
      </div>
      <em>Browse</em>
    </div>
  </label>
);

const DirectoryScreen = ({ people: directoryPeople = directory, searchTerm = "", onSubmitReferral, referralLoading = false }) => {
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralForm, setReferralForm] = useState(initialReferralForm);
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const query = (localSearchTerm || searchTerm).trim().toLowerCase();
  const people = query
    ? directoryPeople.filter((person) =>
        [person.name, person.role, person.email].some((value) => String(value || "").toLowerCase().includes(query))
      )
    : directoryPeople;
  const designationCount = new Set(people.map((person) => person.role).filter(Boolean)).size;

  useEffect(() => {
    if (!searchLoading) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSearchLoading(false), 180);
    return () => window.clearTimeout(timeoutId);
  }, [searchLoading, query]);

  const handleSearchChange = (value) => {
    setSearchLoading(true);
    setLocalSearchTerm(value);
  };

  const closeReferralModal = () => {
    if (referralLoading) {
      return;
    }
    setShowReferralModal(false);
    setReferralForm(initialReferralForm);
    setResumeFile(null);
    setCoverLetterFile(null);
  };

  const handleReferralChange = (field, value) => {
    setReferralForm((current) => ({ ...current, [field]: value }));
  };

  const handleReferralSubmit = async (event) => {
    event.preventDefault();
    if (!onSubmitReferral) {
      return;
    }
    const saved = await onSubmitReferral({
      ...referralForm,
      resumeFile,
      coverLetterFile,
    });
    if (saved) {
      setShowReferralModal(false);
      setReferralForm(initialReferralForm);
      setResumeFile(null);
      setCoverLetterFile(null);
    }
  };

  return (
    <section className="new-hrm-screen">
      <ScreenHeader
        eyebrow="People"
        title="Directory"
        description="Find employees, send email, and submit referrals."
      />
      <section className="new-hrm-panel new-hrm-loader-host">
        <ComponentLoader active={searchLoading} label="Filtering directory" />
        <header className="new-hrm-panel__header new-hrm-panel__header--compact">
          <div className="new-hrm-panel__summary">
            <h2>Employees</h2>
            <p>{people.length} visible people across {designationCount} designation{designationCount === 1 ? "" : "s"}</p>
          </div>
          <div className="new-hrm-directory-actions">
            <label className="new-hrm-directory-search">
              <span>Search Directory</span>
              <input
                value={localSearchTerm}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search name, email, or designation"
              />
            </label>
            <button className="new-hrm-primary-button" type="button" onClick={() => setShowReferralModal(true)}>
              <FiUserPlus aria-hidden="true" />
              <span>Refer Candidate</span>
            </button>
          </div>
        </header>
        <div className="new-hrm-directory-table" role="table" aria-label="Employee directory">
          <div className="new-hrm-directory-table__head" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Email</span>
            <span role="columnheader">Designation</span>
            <span role="columnheader">Action</span>
          </div>
          {people.map((person) => (
            <div key={person.id} className="new-hrm-directory-row" role="row">
              <div
                className="new-hrm-directory-person"
                role="cell"
                data-label="Name"
                aria-label={`Name: ${person.name || "Not provided"}`}
              >
                <Avatar
                  imageUrls={getProfileImageCandidates(person)}
                  initials={getInitials(person.name)}
                  name={person.name}
                />
                <strong>{person.name}</strong>
              </div>
              <span role="cell" data-label="Email" aria-label={`Email: ${person.email || "Not provided"}`}>
                {person.email || "-"}
              </span>
              <span role="cell" data-label="Designation" aria-label={`Designation: ${person.role || "Employee"}`}>
                {person.role || "Employee"}
              </span>
              {person.email ? (
                <div role="cell" data-label="Action">
                  <a
                    className="new-hrm-directory-email-button"
                    href={`mailto:${person.email}`}
                    aria-label={`Send email to ${person.name || person.email}`}
                  >
                    <FiMail aria-hidden="true" />
                    <span>Send Email</span>
                  </a>
                </div>
              ) : (
                <span role="cell" data-label="Action" aria-label={`Email action unavailable for ${person.name || "employee"}`}>
                  -
                </span>
              )}
            </div>
          ))}
          {!people.length && <p className="new-hrm-empty-state">No employees found.</p>}
        </div>
      </section>
      {showReferralModal && (
        <div className="new-hrm-modal-backdrop" role="presentation">
          <form className="new-hrm-modal new-hrm-referral-modal" onSubmit={handleReferralSubmit} role="dialog" aria-modal="true" aria-labelledby="referral-modal-title">
            <header className="new-hrm-modal__header new-hrm-referral-modal__header">
              <div className="new-hrm-referral-title">
                <span className="new-hrm-referral-title__icon">
                  <FiBriefcase aria-hidden="true" />
                </span>
                <div>
                  <span className="new-hrm-referral-kicker">Talent referral</span>
                  <h2 id="referral-modal-title">Refer Candidate</h2>
                  <p>Create a candidate and HR case for recruitment review.</p>
                </div>
              </div>
              <button className="new-hrm-icon-button" type="button" aria-label="Close referral form" onClick={closeReferralModal}>
                <FiX aria-hidden="true" />
              </button>
            </header>
            <div className="new-hrm-referral-layout">
              <section className="new-hrm-referral-main">
                <div className="new-hrm-referral-section-title">
                  <span>Candidate profile</span>
                  <strong>Required details and documents</strong>
                </div>
                <div className="new-hrm-referral-grid">
                  <label className="new-hrm-referral-input">
                    <span className="new-hrm-referral-label">Role</span>
                    <div className="new-hrm-referral-input-shell">
                      <FiBriefcase aria-hidden="true" />
                      <input
                        required
                        maxLength={128}
                        value={referralForm.role}
                        onChange={(event) => handleReferralChange("role", event.target.value)}
                        placeholder="Salesforce Developer"
                      />
                    </div>
                  </label>
                  <label className="new-hrm-referral-input">
                    <span className="new-hrm-referral-label">Candidate Name</span>
                    <div className="new-hrm-referral-input-shell">
                      <FiUser aria-hidden="true" />
                      <input
                        required
                        maxLength={121}
                        value={referralForm.candidateName}
                        onChange={(event) => handleReferralChange("candidateName", event.target.value)}
                        placeholder="Full name"
                      />
                    </div>
                  </label>
                  <label className="new-hrm-referral-input">
                    <span className="new-hrm-referral-label">Email</span>
                    <div className="new-hrm-referral-input-shell">
                      <FiMail aria-hidden="true" />
                      <input
                        required
                        type="email"
                        maxLength={80}
                        value={referralForm.email}
                        onChange={(event) => handleReferralChange("email", event.target.value)}
                        placeholder="candidate@example.com"
                      />
                    </div>
                  </label>
                  <label className="new-hrm-referral-input">
                    <span className="new-hrm-referral-label">Phone</span>
                    <div className="new-hrm-referral-input-shell">
                      <FiPhone aria-hidden="true" />
                      <input
                        maxLength={40}
                        value={referralForm.phone}
                        onChange={(event) => handleReferralChange("phone", event.target.value)}
                        placeholder="+91..."
                      />
                    </div>
                  </label>
                  <ReferralFileField id="new-hrm-referral-resume" label="Resume" required file={resumeFile} onChange={setResumeFile} />
                  <ReferralFileField
                    id="new-hrm-referral-cover-letter"
                    label="Cover Letter"
                    file={coverLetterFile}
                    onChange={setCoverLetterFile}
                  />
                  <label className="new-hrm-referral-input new-hrm-referral-grid__wide">
                    <span className="new-hrm-referral-label">Notes</span>
                    <textarea
                      maxLength={30000}
                      value={referralForm.notes}
                      onChange={(event) => handleReferralChange("notes", event.target.value)}
                      placeholder="Add context for HR"
                    />
                  </label>
                </div>
              </section>
              <aside className="new-hrm-referral-summary">
                <span>CRM output</span>
                <strong>Candidate and HR case</strong>
                <div className="new-hrm-referral-summary__item">
                  <FiUser aria-hidden="true" />
                  <div>
                    <b>Candidate</b>
                    <small>Created in Salesforce</small>
                  </div>
                </div>
                <div className="new-hrm-referral-summary__item">
                  <FiFileText aria-hidden="true" />
                  <div>
                    <b>Referral case</b>
                    <small>Assigned to HR</small>
                  </div>
                </div>
                <div className="new-hrm-referral-summary__item">
                  <FiUploadCloud aria-hidden="true" />
                  <div>
                    <b>Documents</b>
                    <small>Resume and files linked</small>
                  </div>
                </div>
              </aside>
            </div>
            <footer className="new-hrm-referral-actions">
              <span className="new-hrm-referral-actions__meta">
                <FiCheckCircle aria-hidden="true" />
                HR receives the referral case after submit
              </span>
              <div>
                <button className="new-hrm-secondary-button" type="button" disabled={referralLoading} onClick={closeReferralModal}>
                  Cancel
                </button>
                <button className="new-hrm-primary-button" type="submit" disabled={referralLoading}>
                  {referralLoading ? "Submitting..." : "Submit Referral"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
};

export default DirectoryScreen;
